/* eslint-disable import/no-cycle */
import { makeAutoObservable } from 'mobx'
import TagManager from 'react-gtm-module'
import agent, { Arg } from '../agent'
import { check } from '../reusableUtils/Helpers'
import { RootStore } from './rootStore'
import { Country } from '../models/GeneralTypes'

type CampaignsResponse = Awaited<ReturnType<typeof agent.Campaigns.getAll>>
// Campaign type: extract from either array format or paginated object format
export type Campaign = CampaignsResponse extends { campaigns: (infer T)[] }
  ? T
  : CampaignsResponse extends (infer T)[]
  ? T
  : never
type CampaignInsights = Awaited<ReturnType<typeof agent.Campaigns.getCampaign>> & {
  title?: string
}

export type NewCampaign = Arg<typeof agent.Campaigns.create>
export type NewUgcCampaign = NewCampaign & { status: 'draft' | 'published' }
class CampaignStore {
  rootStore: RootStore
  campaigns?: Campaign[]
  total?: number
  hasMore?: boolean

  selectedCampaign?: CampaignInsights

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  async getCampaigns(brandId?: string, limit?: number, offset?: number) {
    if (brandId) {
      const response = await agent.Campaigns.getAll({ brandId: brandId, limit, offset } as any)

      // Handle both old format (array) and new format (object with campaigns, total, hasMore)
      if (Array.isArray(response)) {
        this.campaigns = response
        this.total = response.length
        this.hasMore = false
      } else if (response && typeof response === 'object' && 'campaigns' in response) {
        const paginatedResponse = response as { campaigns: Campaign[]; total: number; hasMore: boolean }
        // When loading more (offset > 0), append to existing campaigns instead of replacing
        if (offset && offset > 0 && this.campaigns) {
          this.campaigns = [...this.campaigns, ...paginatedResponse.campaigns]
        } else {
          this.campaigns = paginatedResponse.campaigns
        }
        this.total = paginatedResponse.total
        this.hasMore = paginatedResponse.hasMore
      }
    }
  }

  async getUgcCampaign(campaignId: string, brandId: string) {
    const data = await agent.Campaigns.getCampaign({ campaignId: campaignId, brandId: brandId })
    if ('applicants' in data) return data
  }

  updateCampaignUGC = agent.Campaigns.update

  setActive(brandId: string, campaignId: string) {
    return agent.Campaigns.setActive({ brandId: brandId, campaignId: campaignId })
  }

  reset() {
    this.campaigns = undefined
    this.selectedCampaign = undefined
    this.total = undefined
    this.hasMore = undefined
  }

  async createUgcCampaign(campaign: NewUgcCampaign) {
    const country = this.rootStore.brandStore.selectedBrand?.brandCountry || Country.germany
    const newCampaign = await agent.Campaigns.create({ ...campaign, brandCountry: country })
    const { brandId, campaignId, amount_paid, invoice_id, items } = newCampaign as NewUgcCampaign & { brandId: string, campaignId: string, amount_paid: number, invoice_id: string, items: unknown[] }

    try {
      const tagManagerArgs = {
        dataLayer: {
          userId: this.rootStore.userStore.authenticatedUser?.userId,
          brandId: brandId,
          campaignId: campaignId,
          price: amount_paid / 100,
          event: 'purchase',
          transaction_id: invoice_id,
          items
        }
      }

      TagManager.dataLayer(tagManagerArgs)
    } catch (e) {
      console.error(e)
    }

    return newCampaign
  }

  async createCampaignFromPackage(campaign: any) {
    const country = this.rootStore.brandStore.selectedBrand?.brandCountry || Country.germany
    const newCampaign = await agent.Brands.createCampaignFromPackage({ ...campaign, brandCountry: country } as any)
    const { brandId, campaignId } = newCampaign as { brandId: string; campaignId: string }

    try {
      const tagManagerArgs = {
        dataLayer: {
          userId: this.rootStore.userStore.authenticatedUser?.userId,
          brandId,
          campaignId,
          event: 'purchase_package'
        }
      }
      TagManager.dataLayer(tagManagerArgs)
    } catch (e) {
      console.error(e)
    }

    return newCampaign
  }

  manageInfluencerContent(brandId: string, campaignId: string, influencerId: string, accept: boolean, denyReasons?: string[]) {
    if (accept) return agent.UGC.approveContent({ brandId, campaignId, influencerId } as Arg<typeof agent.UGC.approveContent>)
    else {
      check(denyReasons, 'Deny reasons are expected')
      return agent.UGC.giveFeedbackToContent({ brandId, campaignId, influencerId, reasons: denyReasons.filterNotNull() } as Arg<typeof agent.UGC.giveFeedbackToContent>)
    }
  }

  getAllFinishedCreators() {
    const brandId = this.rootStore.brandStore.selectedBrand?.brandId
    check(brandId, 'Brand not selected')
    return agent.UGC.getAllFinishedCreators({ brandId })
  }

  upgradeVideo = agent.UGC.upgradeVideo
  setContentFeedback = agent.UGC.setContentFeedback
}

export default CampaignStore
